import { DataManager } from "../model/DataManager.js";
import { VOCategory, VOGalleryList } from "../model/DataStructure.js";
import { Gallery } from "./Gallery.js";
import { Indicator } from "./Indicator.js";
import { AbstractContentView, ArrowState } from "./views/AbstractContentView.js";
import { GalleryContentView } from "./views/GalleryContentView.js";
import { LogbookContentView } from "./views/LogbookContentView.js";
import { MediaGalleryContentView as VideoGalleryContentView } from "./views/VideoGalleryContentView.js";
import { PdfContentView } from "./views/PdfContentView.js";

export class CategoryView {
    //Used for the breadcrumb.
    public onSelectEntry = e5.Signal.create<[string]>();
    public onClose = e5.Signal.create();

    public element: HTMLDivElement;

    private _view: AbstractContentView = null;
    private _naviControl: HTMLDivElement;
    private _prev: HTMLDivElement;
    private _close: HTMLDivElement;
    private _next: HTMLDivElement;
    private _gallery: Gallery;
    private _showArrows: ArrowState;
    private _indicator: Indicator;

    constructor(private _data: DataManager) {
        this.element = document.createElement("div");
        this.element.id = "category-view";
        this.element.innerHTML = `
        <div class="navi-control">
            <div class="prev icon icon-125"></div>
            <div class="close icon icon-607"></div>
            <div class="next icon icon-124"></div>
        </div>
        `;
        this._naviControl = this.element.querySelector(".navi-control");
        this._prev = this.element.querySelector(".prev");
        this._close = this.element.querySelector(".close");
        this._next = this.element.querySelector(".next");

        this._gallery = new Gallery();
        this.element.appendChild(this._gallery.element);

        this._indicator = new Indicator();
        this.element.appendChild(this._indicator.element);

        this._close.addEventListener("click", () => {
            if (!this._view) {
                this.close();
                return;
            }
            if (this.closeGallery()) {
            }
            else if (this._view.handleClickClose()) {
                this.close();
            }
        });

        this._gallery.onChangeShowArrows.add((arrowState) => {
            if (this._gallery.active)
                this.showArrows = arrowState;
        })

        this._prev.addEventListener("click", () => {

            if (this._gallery.active) {
                this._gallery.prev();
                return;
            }

            if (!this.view || this.view.showArrows.left === "none" && this.view.showArrows.right === "none")
                return;
            this.view.prev();
        });

        this._next.addEventListener("click", () => {
            if (this._gallery.active) {
                this._gallery.next();
                return;
            }

            if (!this.view || this.view.showArrows.left === "none" && this.view.showArrows.right === "none")
                return;
            this.view.next();
        });

        this._gallery.onSelectEntry.add((entry) => {
            if (this._gallery.active) {
                this.onSelectEntry.dispatch(entry);
            }
        });

        this._gallery.onClose.add((galleryList, galleryIndex, mediaIndex) => {
            if (this._view) {
                this._view.onCloseGallery.dispatch(galleryList, galleryIndex, mediaIndex);
            }
        });
    }

    public reset(): void {
        for (var category of this._data.data.category) {
            if (category.view) {
                category.view.reset();
            }
        }
    }

    private get showArrows(): ArrowState {
        return this._showArrows;
    }

    private set showArrows(showArrows: ArrowState) {
        this._prev.classList.toggle("disabled", showArrows.left === "none");
        this._next.classList.toggle("disabled", showArrows.right === "none");
        this._prev.classList.toggle("skip", showArrows.left === "skip");
        this._next.classList.toggle("skip", showArrows.right === "skip");

        this._indicator.update(showArrows.index, showArrows.total);
    }

    public get active(): boolean {
        return this.element.classList.contains("active");
    }

    public set active(active: boolean) {
        this.element.classList.toggle("active", active);
        this._naviControl.classList.toggle("active", active);
    }

    public async open(category: VOCategory): Promise<boolean> {
        if (!category) {
            console.warn("Could not open empty category. The given category is null/undefined.");
            return Promise.resolve(false);
        }

        var isNew = !category.view;

        if (!category.view) {
            var view: AbstractContentView = null;
            if (category.kind === "pdf") {
                view = new PdfContentView();
            }
            else if (category.kind === "gallery") {
                view = new GalleryContentView("gallery");
            }
            else if (category.kind === "logbook") {
                view = new LogbookContentView(this._data);
            }
            else if (category.kind === "video_gallery") {
                view = new VideoGalleryContentView();
            }
            else if (category.kind === "media_and_text") {
                category.galleries = null;
                view = new GalleryContentView("media_and_text");
            }
            
            category.view = view;
        }
        
        if (!category.view) {
            console.warn(`Could not find AbstractContentView for category kind "${category.kind}".`);
            // console.warn("Could not create/get view for kind:", category.kind);
            // this.view = null;
            // this.onClose.dispatch();
            this.close();
            return;
        }

        this.view = category.view;
        if (isNew) {
            await category.view.create(category);
        }
        this.view.active = true;
        category.view.open();

        this.active = true;

        return true;
    }

    public close(): void {
        this.closeGallery();

        this.active = false;
        if (this.view)
            this.view.active = false;
        this.onClose.dispatch();

        setTimeout(() => {
            if (!this.active && this.view) {
                this.view = null;
            }
        }, 700);
    }

    private openGallery(galleryList: VOGalleryList, galleryIndex: number, mediaIndex: number): void {
        if (this.view) {
            this.view.blurred = true;
        }
        this.showArrows = {
            left: "normal",
            right: "normal",
            index: 0,
            total: 0
        };
        this._gallery.openGallery(galleryList, galleryIndex, mediaIndex);
    }

    private closeGallery(): boolean {
        if (this._gallery.active) {
            this._gallery.close();
            this.onSelectEntry.dispatch(null);
            if (this.view) {
                this.view.blurred = false;
                this.showArrows = this.view.showArrows;
            } else {
                this.showArrows = {
                    left: "none",
                    right: "none",
                    index: 0,
                    total: 0
                };
            }
            return true;
        }
        return false;
    }

    private get view(): AbstractContentView {
        return this._view;
    }

    private set view(view: AbstractContentView) {
        if (this._view === view) {
            this.closeGallery();
            return;
        }
        if (this._view) {
            this._view.element.remove();
            this._view.onSelectEntry.remove("category-view");
            this._view.onRequestGallery.remove("category-view");
            this._view.onChangeShowArrows.remove("category-view");
            this._view.onClosedEnd.dispatch();
            this._view.blurred = false;
            this._view.active = false;
            this._gallery.close();
        }
        this._view = view;
        if (this._view) {
            this.showArrows = this._view.showArrows;
            this.element.prepend(this._view.element);
            this._view.blurred = false;
            this._view.active = true;
            this._view.onRequestGallery.add(this.openGallery, this, "category-view");
            this._view.onSelectEntry.add((entry) => this.onSelectEntry.dispatch(entry), this, "category-view");
            this._view.onChangeShowArrows.add((showArrows) => this.showArrows = showArrows, this, "category-view");
        } else {
            this.showArrows = {
                left: "none",
                right: "none",
                index: 0,
                total: 0
            };
        }
    }
}